import { useEffect, useMemo, useState } from 'react';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { CheckboxField, TextField } from '../components/Form';
import { Chip } from '../components/Chips';
import { CardSkeleton, EmptyState, ErrorState, InlineError, TableSkeleton } from '../components/Feedback';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { ConfirmDialog } from '../components/Overlay';
import { useAuth } from '../lib/auth';
import { partnerDirectory, partnerEntitlements } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import type { ProfileDraftDto, ProfileDraftEdit } from '../lib/types';

type DraftColumn = 'version' | 'status' | 'company' | 'approved' | 'published';

function draftSortValue(draft: ProfileDraftDto, key: DraftColumn): string | number {
  switch (key) {
    case 'version':
      return draft.version;
    case 'status':
      return draft.status;
    case 'company':
      return draft.companyName;
    case 'approved':
      return draft.approvedUtc ?? '';
    case 'published':
      return draft.publishedUtc ?? '';
    default:
      return '';
  }
}

/**
 * A per-field "Visible" checkbox, mirroring Business Central's own "Dir. Setup iER" checkboxes:
 * a field can hold a value while still being withheld from anyone who isn't this company reading
 * its own profile back. Rendered in a field's badge slot so the choice sits right next to the
 * value it governs, rather than in a separate list an operator has to cross-reference.
 */
function VisibilityToggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="visibility-toggle">
      <input
        type="checkbox"
        className="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      Visible
    </label>
  );
}

/**
 * THE COMPANY DIRECTORY — what this company chooses to disclose to other iERP companies.
 *
 * The single idea this screen has to make unmissable is that NOTHING here is visible to anyone
 * else until Publish. A draft is private. An APPROVED draft is still private - approval records
 * that a person agreed to the disclosure, and that is all it does. Publish is the only act that
 * sends anything to the central directory, and it is the only one presented as consequential.
 *
 * The draft is built FROM QuickBooks company information rather than typed from nothing, so the
 * starting point is what QuickBooks already holds; the operator then removes or corrects whatever
 * should not be disclosed. That ordering matters: a blank form invites inventing a company
 * identity, and a pre-filled one invites reviewing a real one.
 *
 * Role split mirrors the server exactly - drafting and editing are operator work
 * (GatewayPolicies.OperatorOrAbove), approving and publishing are administrator decisions
 * (AdminOnly). The buttons are hidden when the role does not hold the permission, but the server
 * is still the authority; a hidden button is a courtesy, never the control.
 */
export function CompanyDirectoryPage(): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();

  const canDraft = can('directory.draft');
  const canPublish = can('directory.publish');

  const drafts = useAsync((signal) => partnerDirectory.drafts(signal), []);
  const entitlement = useAsync((signal) => partnerEntitlements.get(signal), []);

  // Read separately and allowed to fail: a company that has never published has no central entry,
  // and that is an ordinary state rather than an error worth blocking the page for.
  const central = useAsync(
    (signal) => partnerDirectory.central(signal).catch(() => null),
    [],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileDraftEdit>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState<ProfileDraftDto | null>(null);

  const draftsTable = useSortableTable<DraftColumn>({
    storageKey: 'companyDirectoryDraftsColumnWidths',
    columns: {
      version: { minWidth: 80 },
      status: { minWidth: 110 },
      company: { minWidth: 160 },
      approved: { minWidth: 130 },
      published: { minWidth: 130 },
    },
  });

  const selected = useMemo(
    () => drafts.data?.find((d) => d.id === selectedId) ?? null,
    [drafts.data, selectedId],
  );

  // The newest editable draft is the one an operator almost always wants, so it opens selected.
  useEffect(() => {
    if (selectedId !== null || !drafts.data || drafts.data.length === 0) return;
    const editable = drafts.data.find((d) => d.status === 'Draft') ?? drafts.data[0];
    setSelectedId(editable.id);
  }, [drafts.data, selectedId]);

  useEffect(() => {
    if (!selected) {
      setForm({});
      return;
    }

    setForm({
      companyName: selected.companyName,
      tradeName: selected.tradeName,
      address: selected.address,
      address2: selected.address2,
      city: selected.city,
      county: selected.county,
      postCode: selected.postCode,
      countryRegionCode: selected.countryRegionCode,
      contactName: selected.contactName,
      contactEmail: selected.contactEmail,
      phoneNo: selected.phoneNo,
      registrationNo: selected.registrationNo,
      vatRegistrationNo: selected.vatRegistrationNo,
      taxRegistrationNo: selected.taxRegistrationNo,
      businessEmailDomain: selected.businessEmailDomain,
      homePage: selected.homePage,
      showInDirectory: selected.showInDirectory,

      showCompanyName: selected.showCompanyName,
      showTradeName: selected.showTradeName,
      showAddress: selected.showAddress,
      showCity: selected.showCity,
      showCounty: selected.showCounty,
      showPostCode: selected.showPostCode,
      showCountryRegionCode: selected.showCountryRegionCode,
      showBusinessEmailDomain: selected.showBusinessEmailDomain,
      showContactName: selected.showContactName,
      showContactEmail: selected.showContactEmail,
      showPhoneNo: selected.showPhoneNo,
      showRegistrationNo: selected.showRegistrationNo,
      showVatRegistrationNo: selected.showVatRegistrationNo,
      showHomePage: selected.showHomePage,
    });
    setError(null);
  }, [selected]);

  const set = (key: keyof ProfileDraftEdit) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value === '' ? null : value }));

  const setVisible = (key: keyof ProfileDraftEdit) => (checked: boolean) =>
    setForm((current) => ({ ...current, [key]: checked }));

  const run = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      toast.success(label);
      await drafts.reload();
      await central.reload();
    } catch (caught) {
      const message = errorMessage(caught);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const editable = selected?.status === 'Draft';

  return (
    <>
      <PageHeader
        title="Company Directory"
        description="What this company discloses to other iERP companies. Nothing leaves here until it is published."
        actions={
          canDraft ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() =>
                void run('Draft built from QuickBooks company information.', async () => {
                  const created = await partnerDirectory.buildDraft();
                  setSelectedId(created.id);
                })
              }
            >
              Build draft from QuickBooks
            </button>
          ) : undefined
        }
      />

      {/*
        The entitlement answer comes from iERP AI, not from a local guess. Showing the refusal
        message it actually gave is more use than hiding the feature: "not entitled" and "iERP AI
        could not be reached" need different actions from the reader.
      */}
      {entitlement.data && !entitlement.data.companyDirectory.enabled ? (
        <Panel title="The company directory is not available to this company">
          <p className="muted">
            {entitlement.data.companyDirectory.message
              ?? 'iERP AI did not grant this company access to the trading-partner directory.'}
          </p>
          <p className="muted">
            Checked {formatDateTime(entitlement.data.companyDirectory.checkedUtc)}. Drafts below can
            still be prepared and approved; publishing needs the entitlement.
          </p>
        </Panel>
      ) : null}

      <Panel title="What the central directory holds today">
        {central.loading ? (
          <CardSkeleton />
        ) : central.data ? (
          <>
            <Toolbar>
              <Chip tone="good">Published</Chip>
              {central.data.anonymousDirectoryEntry ? (
                <Chip tone="warn">Anonymous entry</Chip>
              ) : null}
            </Toolbar>
            <dl className="detail-list">
              <dt>Company</dt>
              <dd>{central.data.companyName ?? '—'}</dd>
              <dt>Trade name</dt>
              <dd>{central.data.tradeName ?? '—'}</dd>
              <dt>Location</dt>
              <dd>
                {[central.data.city, central.data.county, central.data.countryRegionCode]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </dd>
              <dt>Business email domain</dt>
              <dd>{central.data.businessEmailDomain ?? '—'}</dd>
              <dt>Profile id</dt>
              <dd className="mono">{central.data.profileId ?? '—'}</dd>
            </dl>
          </>
        ) : (
          <EmptyState
            title="Not published"
            description="No other company can see this company in the directory. Publish an approved draft to appear in partner searches."
          />
        )}
      </Panel>

      <Panel title="Drafts">
        {drafts.loading && !drafts.data ? (
          <TableSkeleton rows={3} columns={5} />
        ) : drafts.error ? (
          <ErrorState message="Drafts could not be loaded." detail={drafts.error} onRetry={drafts.reload} />
        ) : !drafts.data || drafts.data.length === 0 ? (
          <EmptyState
            title="No drafts yet"
            description={
              canDraft
                ? 'Build a draft from QuickBooks company information, correct what should not be disclosed, then approve and publish it.'
                : 'An operator builds the first draft from QuickBooks company information.'
            }
          />
        ) : (
          <TableWrap tableClassName="data-table-sortable">
            <colgroup>
              <col style={draftsTable.columnStyle('version')} />
              <col style={draftsTable.columnStyle('status')} />
              <col style={draftsTable.columnStyle('company')} />
              <col style={draftsTable.columnStyle('approved')} />
              <col style={draftsTable.columnStyle('published')} />
              <col className="col-actions" />
            </colgroup>
            <thead>
              <tr>
                <SortableTh
                  label="Version"
                  sortKey="version"
                  currentSortKey={draftsTable.sortKey}
                  sortDir={draftsTable.sortDir}
                  onSort={draftsTable.toggleSort}
                  style={draftsTable.columnStyle('version')}
                  onResizeStart={draftsTable.beginResize('version')}
                />
                <SortableTh
                  label="Status"
                  sortKey="status"
                  currentSortKey={draftsTable.sortKey}
                  sortDir={draftsTable.sortDir}
                  onSort={draftsTable.toggleSort}
                  style={draftsTable.columnStyle('status')}
                  onResizeStart={draftsTable.beginResize('status')}
                />
                <SortableTh
                  label="Company"
                  sortKey="company"
                  currentSortKey={draftsTable.sortKey}
                  sortDir={draftsTable.sortDir}
                  onSort={draftsTable.toggleSort}
                  style={draftsTable.columnStyle('company')}
                  onResizeStart={draftsTable.beginResize('company')}
                />
                <SortableTh
                  label="Approved"
                  sortKey="approved"
                  currentSortKey={draftsTable.sortKey}
                  sortDir={draftsTable.sortDir}
                  onSort={draftsTable.toggleSort}
                  style={draftsTable.columnStyle('approved')}
                  onResizeStart={draftsTable.beginResize('approved')}
                />
                <SortableTh
                  label="Published"
                  sortKey="published"
                  currentSortKey={draftsTable.sortKey}
                  sortDir={draftsTable.sortDir}
                  onSort={draftsTable.toggleSort}
                  style={draftsTable.columnStyle('published')}
                  onResizeStart={draftsTable.beginResize('published')}
                />
                <th scope="col" className="col-actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {draftsTable.sort(drafts.data, draftSortValue).map((draft) => (
                <tr key={draft.id} className={draft.id === selectedId ? 'row-selected' : undefined}>
                  <td className="num">{draft.version}</td>
                  <td>
                    <Chip
                      tone={
                        draft.status === 'Published'
                          ? 'good'
                          : draft.status === 'Approved'
                            ? 'info'
                            : 'neutral'
                      }
                    >
                      {draft.status}
                    </Chip>
                    {draft.status !== 'Published' ? (
                      <span className="cell-sub">discloses nothing yet</span>
                    ) : null}
                  </td>
                  <td>
                    <span className="cell-strong">{draft.companyName}</span>
                    <span className="cell-sub">
                      {draft.showInDirectory ? 'listed in searches' : 'hidden from searches'}
                    </span>
                  </td>
                  <td>
                    {draft.approvedUtc ? formatDateTime(draft.approvedUtc) : '—'}
                    <span className="cell-sub">{draft.approvedByEmail ?? ''}</span>
                  </td>
                  <td>
                    {draft.publishedUtc ? formatDateTime(draft.publishedUtc) : '—'}
                    <span className="cell-sub">{draft.publishMessage ?? ''}</span>
                  </td>
                  <td className="col-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setSelectedId(draft.id)}
                    >
                      {draft.status === 'Draft' ? 'Edit' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      {selected ? (
        <Panel
          title={`Version ${selected.version} — ${selected.status}`}
          description={
            editable
              ? 'Correct anything that should not be disclosed. Saving changes nothing outside this company.'
              : 'This version is no longer editable. Build a new draft to change what is disclosed.'
          }
        >
          <InlineError message={error} />

          <p className="field-hint">
            The "Visible" checkbox on each field decides whether a company that finds or connects
            to this profile actually sees it — a field can be filled in and still withheld.
          </p>

          <div className="form-grid">
            <TextField
              label="Company name"
              value={form.companyName ?? ''}
              onChange={set('companyName')}
              required
              disabled={!editable}
              className="form-span-2"
              badge={<VisibilityToggle checked={form.showCompanyName ?? true} onChange={setVisible('showCompanyName')} disabled={!editable} />}
            />
            <TextField
              label="Trade name"
              value={form.tradeName ?? ''}
              onChange={set('tradeName')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showTradeName ?? true} onChange={setVisible('showTradeName')} disabled={!editable} />}
            />
            <TextField
              label="Business email domain"
              value={form.businessEmailDomain ?? ''}
              onChange={set('businessEmailDomain')}
              disabled={!editable}
              hint="How partners recognise email from this company."
              badge={<VisibilityToggle checked={form.showBusinessEmailDomain ?? true} onChange={setVisible('showBusinessEmailDomain')} disabled={!editable} />}
            />
            <TextField
              label="Address"
              value={form.address ?? ''}
              onChange={set('address')}
              disabled={!editable}
              hint="Covers Address 2 as well — one visibility choice for both lines."
              badge={<VisibilityToggle checked={form.showAddress ?? true} onChange={setVisible('showAddress')} disabled={!editable} />}
            />
            <TextField label="Address 2" value={form.address2 ?? ''} onChange={set('address2')} disabled={!editable} />
            <TextField
              label="City"
              value={form.city ?? ''}
              onChange={set('city')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showCity ?? true} onChange={setVisible('showCity')} disabled={!editable} />}
            />
            <TextField
              label="County / state"
              value={form.county ?? ''}
              onChange={set('county')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showCounty ?? true} onChange={setVisible('showCounty')} disabled={!editable} />}
            />
            <TextField
              label="Post code"
              value={form.postCode ?? ''}
              onChange={set('postCode')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showPostCode ?? true} onChange={setVisible('showPostCode')} disabled={!editable} />}
            />
            <TextField
              label="Country code"
              value={form.countryRegionCode ?? ''}
              onChange={set('countryRegionCode')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showCountryRegionCode ?? true} onChange={setVisible('showCountryRegionCode')} disabled={!editable} />}
            />
            <TextField
              label="Contact name"
              value={form.contactName ?? ''}
              onChange={set('contactName')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showContactName ?? false} onChange={setVisible('showContactName')} disabled={!editable} />}
            />
            <TextField
              label="Contact email"
              type="email"
              value={form.contactEmail ?? ''}
              onChange={set('contactEmail')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showContactEmail ?? false} onChange={setVisible('showContactEmail')} disabled={!editable} />}
            />
            <TextField
              label="Phone"
              value={form.phoneNo ?? ''}
              onChange={set('phoneNo')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showPhoneNo ?? false} onChange={setVisible('showPhoneNo')} disabled={!editable} />}
            />
            <TextField
              label="Registration no."
              value={form.registrationNo ?? ''}
              onChange={set('registrationNo')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showRegistrationNo ?? false} onChange={setVisible('showRegistrationNo')} disabled={!editable} />}
            />
            <TextField
              label="VAT registration no."
              value={form.vatRegistrationNo ?? ''}
              onChange={set('vatRegistrationNo')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showVatRegistrationNo ?? false} onChange={setVisible('showVatRegistrationNo')} disabled={!editable} />}
            />
            <TextField
              label="Tax registration no."
              value={form.taxRegistrationNo ?? ''}
              onChange={set('taxRegistrationNo')}
              disabled={!editable}
              hint="Used by partners to confirm they found the right company. Always visible to a match — the central directory has no separate switch for this one."
            />
            <TextField
              label="Home page"
              value={form.homePage ?? ''}
              onChange={set('homePage')}
              disabled={!editable}
              badge={<VisibilityToggle checked={form.showHomePage ?? false} onChange={setVisible('showHomePage')} disabled={!editable} />}
            />
          </div>

          <CheckboxField
            label="List this company in partner searches"
            checked={form.showInDirectory ?? false}
            onChange={(checked) => setForm((current) => ({ ...current, showInDirectory: checked }))}
            disabled={!editable}
            hint="Unlisted companies can still be connected to by a partner who already knows their profile id."
          />

          <Toolbar>
            {editable && canDraft ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() =>
                  void run('Draft saved. Nothing has been disclosed.', () =>
                    partnerDirectory.updateDraft(selected.id, form),
                  )
                }
              >
                {busy ? 'Saving…' : 'Save draft'}
              </button>
            ) : null}

            {selected.status === 'Draft' && canPublish ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() =>
                  void run('Disclosure approved. Still not published.', () =>
                    partnerDirectory.approveDraft(selected.id),
                  )
                }
              >
                Approve the disclosure
              </button>
            ) : null}

            {selected.status === 'Approved' && canPublish ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => setConfirmPublish(selected)}
              >
                Publish to the directory
              </button>
            ) : null}
          </Toolbar>

          {selected.status === 'Approved' ? (
            <p className="drawer-note">
              Approved by {selected.approvedByEmail ?? 'an administrator'} on{' '}
              {selected.approvedUtc ? formatDateTime(selected.approvedUtc) : '—'}. This is still
              private — publishing is what makes it visible to other companies.
            </p>
          ) : null}
        </Panel>
      ) : null}

      {/*
        The only irreversible-feeling step on the page gets the only confirmation. Everything else
        here is private to this company, so a dialog on Save or Approve would be noise that teaches
        the operator to click through dialogs.
      */}
      <ConfirmDialog
        open={confirmPublish !== null}
        title="Publish this company to the directory?"
        message={
          `Version ${confirmPublish?.version ?? ''} of ${confirmPublish?.companyName ?? 'this company'} `
          + 'will be sent to the central iERP directory, where other companies can find it and '
          + 'request a connection. The central store versions it from there.'
        }
        confirmLabel="Publish"
        onCancel={() => setConfirmPublish(null)}
        onConfirm={() => {
          const draft = confirmPublish;
          setConfirmPublish(null);
          if (draft) {
            void run('Published to the central directory.', () =>
              partnerDirectory.publishDraft(draft.id),
            );
          }
        }}
      />
    </>
  );
}
